import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getPersonSchedule, getDemoData, respondToAssignment } from '../../utils/api';
import { personScheduleLabel } from '../../utils/scheduleFormat';
import PrintScheduleModal from '../modals/PrintScheduleModal';
import KidsDailyDetails from '../kids/KidsDailyDetails';
import KidsDetailSheets from '../kids/KidsDetailSheets';
import KidsRotationsTab from '../kids/KidsRotationsTab';
import KidsAllergies from '../kids/KidsAllergies';

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  } catch { return dateStr; }
}

// Make Google Docs/Sheets/Slides/Drive links embeddable in an iframe — Google
// blocks framing of /edit and /view URLs, so swap to /preview. Other URLs pass
// through unchanged. (The "Open in new tab" links still use the original URL.)
function embedUrl(url) {
  if (!url) return url;
  const u = String(url).trim();
  let m = u.match(/^(https:\/\/docs\.google\.com\/(?:document|spreadsheets|presentation)\/d\/[^/]+)/);
  if (m) return m[1] + '/preview';
  m = u.match(/^(https:\/\/drive\.google\.com\/file\/d\/[^/]+)/);
  if (m) return m[1] + '/preview';
  return u;
}

const DAY_ORDER = ['Thursday', 'Friday', 'Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday'];

function groupByDay(items) {
  // Group by DATE when one exists and order groups chronologically — a
  // day-name-only ordering put pre-conference days (Wed 9/16) AFTER Sunday
  // and merged the two Saturdays into one block. Date-less items keep the
  // classic conference day order, after the dated days.
  const map = {};
  for (const item of items) {
    const key = item.date || item.day || 'Other';
    if (!map[key]) map[key] = { key, day: item.day || '', date: item.date || '', items: [] };
    if (!map[key].day && item.day) map[key].day = item.day;
    map[key].items.push(item);
  }
  const groups = Object.values(map);
  for (const g of groups) {
    g.items.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
    if (!g.day && g.date) {
      const d = new Date(g.date + 'T00:00:00');
      if (!isNaN(d)) g.day = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getDay()];
    }
  }
  groups.sort((a, b) => {
    if (a.date && b.date) return a.date.localeCompare(b.date);
    if (a.date) return -1;
    if (b.date) return 1;
    const ra = DAY_ORDER.indexOf(a.day), rb = DAY_ORDER.indexOf(b.day);
    return (ra === -1 ? 99 : ra) - (rb === -1 ? 99 : rb);
  });
  return groups;
}

// sc-pill colors cycling for multi-team (matches v3 palette)
const TEAM_PILL_STYLES = [
  { background: '#D4E8DF', color: '#2D6B55' },
  { background: '#EDE0D4', color: '#7A5235' },
  { background: '#E2D8EC', color: '#5A3A7A' },
  { background: '#D8E6E0', color: '#2A5446' },
  { background: '#DDEAF5', color: '#1E4870' },
];

function getTeamPillStyle(teamId, teamIds) {
  const idx = teamIds.indexOf(teamId);
  return TEAM_PILL_STYLES[idx % TEAM_PILL_STYLES.length];
}

// Preset block palette (mirrors .sc-b1..sc-b12 in index.css). A team item's
// linked-block bubble cycles through these so each block name keeps a
// consistent pretty color.
const BLOCK_PILL_STYLES = [
  { background: '#D4E8DF', color: '#2D6B55' },
  { background: '#EDE0D4', color: '#7A5235' },
  { background: '#E2D8EC', color: '#5A3A7A' },
  { background: '#D8E6E0', color: '#2A5446' },
  { background: '#DDEAF5', color: '#1E4870' },
  { background: '#F5EDD8', color: '#6A4818' },
  { background: '#F0DDE8', color: '#6E2248' },
  { background: '#D8EAD0', color: '#2A5020' },
  { background: '#E8F0FA', color: '#1A3A6E' },
  { background: '#FAE8D0', color: '#7A3A10' },
  { background: '#F8E8FA', color: '#6A1A7A' },
  { background: '#D8F0EC', color: '#1A5048' },
];

function blockPillStyle(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return BLOCK_PILL_STYLES[h % BLOCK_PILL_STYLES.length];
}

const DEMO_DATA = {
  person: {
    id: 'demo',
    name: 'Daniel Smith',
    email: 'daniel@email.com',
    teams: [{ id: 'conc', name: 'Concourse Team' }],
    roles: { conc: ['Concourse Lead'] },
    passLevel: 'SL Pass',
    teamIds: ['conc'],
  },
  schedule: [
    { blockId: 'd1', assignmentId: 'a1', blockTitle: 'Shuttle Depart', day: 'Thursday', date: '2026-09-10', startTime: '07:00', endTime: '', teamId: 'conc', teamName: 'Concourse Team', positionLabel: 'Hotel Lobby', status: 'accepted', isTransportation: true },
    { blockId: 'd2', assignmentId: 'a2', blockTitle: 'All-Hands Briefing', day: 'Thursday', date: '2026-09-10', startTime: '08:30', endTime: '', teamId: 'conc', teamName: 'Concourse Team', positionLabel: 'Main Auditorium', status: 'accepted' },
    { blockId: 'd3', assignmentId: 'a3', blockTitle: 'Concourse Setup', day: 'Thursday', date: '2026-09-10', startTime: '09:30', endTime: '', teamId: 'conc', teamName: 'Concourse Team', positionLabel: 'Concourse Level 2', status: 'accepted' },
    { blockId: 'd4', assignmentId: 'a4', blockTitle: 'Lunch', day: 'Thursday', date: '2026-09-10', startTime: '12:00', endTime: '', teamId: 'conc', teamName: 'Concourse Team', positionLabel: 'Green Room', status: 'accepted', isMeal: true },
    { blockId: 'd6', assignmentId: 'a6', blockTitle: 'Shuttle Depart', day: 'Friday', date: '2026-09-11', startTime: '07:30', endTime: '', teamId: 'conc', teamName: 'Concourse Team', positionLabel: 'Hotel Lobby', status: 'accepted', isTransportation: true },
    { blockId: 'd7', assignmentId: 'a7', blockTitle: 'Concourse Open — Session 2', day: 'Friday', date: '2026-09-11', startTime: '09:00', endTime: '', teamId: 'conc', teamName: 'Concourse Team', positionLabel: 'Concourse Level 2', status: 'accepted' },
  ],
  teamDocs: {
    conc: [
      { id: 'team-manual', label: 'Team Manual', url: 'https://example.com' },
      { id: 'doc-2', label: 'Day-Of Runsheet', url: 'https://example.com/runsheet' },
    ],
  },
  slManual: { url: 'https://example.com/sl-manual', label: 'SL Manual 2026' },
  teamBlocks: {
    conc: [
      { blockTitle: 'Shuttle Depart', day: 'Thursday', date: '2026-09-10', startTime: '07:00', endTime: '', notes: 'Hotel Lobby' },
      { blockTitle: 'All-Hands Briefing', day: 'Thursday', date: '2026-09-10', startTime: '08:30', endTime: '', notes: 'Main Auditorium' },
      { blockTitle: 'Concourse Open — Session 2', day: 'Friday', date: '2026-09-11', startTime: '09:00', endTime: '', notes: 'Concourse Level 2' },
    ],
  },
};

// ── Kids demos ────────────────────────────────────────────────────────────────
// Two separate preview links so Mal can see both experiences without touching
// real people:
//   /s/kids-sgl-demo — a SMALL GROUP LEADER (My Group tab with kids/allergies)
//   /s/kids-sl-demo  — a regular Kids servant leader (no group tab)
// Both are fake data and fully interactive; nothing saves.

const KIDS_DEMO_BASE = {
  schedule: [
    { blockId: 'k1', assignmentId: 'ka1', blockTitle: 'Elementary — Session 1', day: 'Friday', date: '2026-09-11', startTime: '09:00', endTime: '11:30', teamId: 'kids', teamName: 'Kids', positionLabel: 'Small Group Leader', status: 'accepted' },
    { blockId: 'k2', assignmentId: 'ka2', blockTitle: 'Elementary — Session 2', day: 'Friday', date: '2026-09-11', startTime: '16:00', endTime: '18:30', teamId: 'kids', teamName: 'Kids', positionLabel: 'Small Group Leader', status: 'pending' },
    { blockId: 'k3', blockTitle: 'Dinner', day: 'Friday', date: '2026-09-11', startTime: '18:30', endTime: '', teamId: '', teamName: '', positionLabel: '', status: 'accepted', isMeal: true, isKidsMeal: true, department: 'Kids' },
    { blockId: 'k4', assignmentId: 'ka4', blockTitle: 'Elementary — Session 1', day: 'Saturday', date: '2026-09-12', startTime: '09:00', endTime: '11:30', teamId: 'kids', teamName: 'Kids', positionLabel: 'Small Group Leader', status: 'accepted' },
  ],
  teamDocs: {
    kids: [
      { id: 'team-manual', label: 'Kids Team Manual', url: 'https://example.com' },
    ],
  },
  slManual: { url: 'https://example.com/sl-manual', label: 'SL Manual 2026' },
  teamBlocks: {
    kids: [
      { blockTitle: 'Elementary — Session 1', day: 'Friday', date: '2026-09-11', startTime: '09:00', endTime: '11:30', notes: 'Kids Wing A' },
      { blockTitle: 'Elementary — Session 2', day: 'Friday', date: '2026-09-11', startTime: '16:00', endTime: '18:30', notes: 'Kids Wing A' },
      { blockTitle: 'Elementary — Session 1', day: 'Saturday', date: '2026-09-12', startTime: '09:00', endTime: '11:30', notes: 'Kids Wing A' },
    ],
  },
  onKidsTeam: true,
};

const KIDS_SGL_DEMO_DATA = {
  ...KIDS_DEMO_BASE,
  person: {
    id: 'kids-sgl-demo',
    name: 'Jordan Lee',
    email: 'jordan@email.com',
    teams: [{ id: 'kids', name: 'Kids' }],
    roles: { kids: ['Elementary'] },
    passLevel: 'Kids Pass',
    teamIds: ['kids'],
  },
  kidsSmallGroup: {
    groupId: 'demo-pink',
    groupName: '1st-2nd Girls',
    groupColor: '#EC6FAA',
    program: 'Elementary',
    children: [
      { name: 'Brilynn Parker', grade: '1', gender: 'Girl', allergies: 'Gluten-Free', parentName: 'Katie Parker', parentContact: '5129237442', isNew: false },
      { name: 'Ellie Smith', grade: '2', gender: 'Girl', allergies: '', parentName: 'Heather Smith', parentContact: '6155045249', isNew: true },
      { name: 'Maddie Johnson', grade: '1', gender: 'Girl', allergies: 'Nut Allergy (peanuts + tree nuts)', parentName: 'Lauren Johnson', parentContact: '6152281744', isNew: false },
      { name: 'Nora Bell', grade: '2', gender: 'Girl', allergies: '', parentName: 'Amber Bell', parentContact: '9315556104', isNew: false },
    ],
    rotationPath: [
      { session: 'Friday 1', activity: 'Games' },
      { session: 'Friday 2', activity: 'Crafts' },
      { session: 'Saturday 1', activity: 'Snack' },
    ],
  },
};

const KIDS_SL_DEMO_DATA = {
  ...KIDS_DEMO_BASE,
  schedule: KIDS_DEMO_BASE.schedule.map(i => ({ ...i, positionLabel: i.positionLabel ? 'Activity Leader' : i.positionLabel })),
  person: {
    id: 'kids-sl-demo',
    name: 'Casey Kim',
    email: 'casey@email.com',
    teams: [{ id: 'kids', name: 'Kids' }],
    roles: { kids: ['Elementary'] },
    passLevel: 'Kids Pass',
    teamIds: ['kids'],
  },
  kidsSmallGroup: null,
};

const DEMO_MAP = {
  'demo': DEMO_DATA,
  'kids-sgl-demo': KIDS_SGL_DEMO_DATA,
  'kids-sl-demo': KIDS_SL_DEMO_DATA,
};

// ── Schedule sub-view ─────────────────────────────────────────────────────────

function ScheduleView({ person, scheduleItems, personId, isDemo, mode = 'schedule' }) {
  const rawTeams = person?.teams || [];
  // Support both { id, name } objects and plain strings (legacy)
  const teamIds = rawTeams.map(t => (typeof t === 'object' ? t.id : t));
  const teamNames = rawTeams.map(t => (typeof t === 'object' ? t.name : t));
  const isMultiTeam = teamIds.length > 1;

  // ── Accept / decline state ──────────────────────────────────────
  // responses overrides the server status for items the SL acts on this session.
  const [responses, setResponses] = useState({});
  const [declineTarget, setDeclineTarget] = useState(null); // assignmentId being declined
  const [declineReason, setDeclineReason] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [errMsg, setErrMsg] = useState('');

  const effStatus = (item) => responses[item.assignmentId] || item.status;

  async function respond(assignmentId, status, reason) {
    if (!assignmentId) return;
    setErrMsg('');
    setResponses(prev => ({ ...prev, [assignmentId]: status }));
    if (isDemo) return;
    setBusyId(assignmentId);
    try {
      await respondToAssignment(assignmentId, person?.id, status, reason || '');
    } catch (e) {
      console.error(e);
      setResponses(prev => { const n = { ...prev }; delete n[assignmentId]; return n; });
      setErrMsg('Something went wrong saving your response. Please try again.');
    } finally {
      setBusyId(null);
    }
  }

  function confirmDecline() {
    const id = declineTarget;
    setDeclineTarget(null);
    const reason = declineReason;
    setDeclineReason('');
    respond(id, 'declined', reason);
  }

  // Positions tab hides unsent (no request to answer yet); My Schedule shows
  // everything it's given — an assigned slot is on their schedule from the
  // moment the lead places them, sent or not.
  const filtered = mode === 'positions'
    ? scheduleItems.filter(i => i.status !== 'unsent')
    : scheduleItems;
  const grouped = groupByDay(filtered);
  const pendingCount = filtered.filter(i => i.assignmentId && effStatus(i) === 'pending').length;

  // Both-server merged schedule: tag each item by department so Kids and main
  // commitments are visually distinct (teal vs dark) and double-booking is obvious.
  const depts = Array.from(new Set(filtered.map(i => i.department).filter(Boolean)));
  const isMultiDept = depts.length > 1;

  const S = {
    wrap: { maxWidth: 660, margin: '0 auto', padding: '36px 32px 60px' },
    kicker: { fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 },
    name: { fontFamily: 'var(--font-h)', fontSize: 34, fontWeight: 900, color: 'var(--black)', lineHeight: 1.1, marginBottom: 6 },
    meta: { fontFamily: 'var(--font-b)', fontSize: 14, fontWeight: 300, color: 'var(--muted)', marginBottom: 36 },
    dayBlock: { marginBottom: 36 },
    dayLabel: { fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--black)', paddingBottom: 10, borderBottom: '2px solid var(--black)', marginBottom: 0 },
    item: { display: 'flex', gap: 20, padding: '16px 0', borderBottom: '1px solid var(--border)', alignItems: 'flex-start' },
    time: { fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, color: 'var(--muted)', width: 80, flexShrink: 0, paddingTop: 3 },
    detail: { flex: 1 },
    itemName: { fontFamily: 'var(--font-h)', fontSize: 15, fontWeight: 800, color: 'var(--black)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    itemLoc: { fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)' },
    pill: { fontFamily: 'var(--font-h)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', borderRadius: 100, padding: '3px 9px', whiteSpace: 'nowrap', flexShrink: 0 },
    footer: { borderTop: '1px solid var(--border)', paddingTop: 20, marginTop: 8 },
    footerNote: { fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)' },
    actions: { display: 'flex', gap: 8, marginTop: 10 },
    statusBadge: { fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 10, display: 'inline-block' },
  };

  const metaParts = [];
  if (teamNames.length > 0) metaParts.push(teamNames.join(' · '));
  if (person?.passLevel) metaParts.push(person.passLevel);

  // Print is deliberately NOT for every servant leader — only allowlisted
  // people see the button (plus /s/<id>/print on the URL for an ad-hoc
  // grant without a deploy).
  const PRINT_SCHEDULE_PIDS = ['P-120303837'];
  const canPrint = mode === 'schedule' && (
    PRINT_SCHEDULE_PIDS.includes(personId) ||
    (typeof window !== 'undefined' && /\/print\/?$/.test(window.location.pathname))
  );
  const [printOpen, setPrintOpen] = useState(false);
  const printGroups = grouped.map(g => ({
    day: g.items[0]?.date ? `${g.day} — ${formatDate(g.items[0].date)}` : g.day,
    items: g.items.map(i => ({
      start: i.startTime,
      end: i.endTime,
      activity: i.blockTitle,
      location: i.positionLabel || '',
      description: i.description || '',
      linked: i.isTeamItem && i.linkedBlock ? String(i.linkedBlock).split(',').map(s => s.trim()).filter(Boolean) : [],
    })),
  }));

  return (
    <div style={S.wrap}>
      <div style={{ marginBottom: 32 }}>
        <div style={S.kicker}>The Belonging Co · Annual Conference 2026</div>
        {mode === 'positions' ? (
          <>
            <div style={S.name}>My Positions</div>
            <div style={S.meta}>Accept or decline each position you've been assigned. Your schedule fills in as you accept.</div>
          </>
        ) : (
          <>
            <div style={S.name}>{person?.name || 'Your Schedule'}</div>
            {metaParts.length > 0 && (
              <div style={S.meta}>{metaParts.join(' · ')}</div>
            )}
            {canPrint && (
              <button className="btn btn-sm" style={{ marginTop: -20, marginBottom: 16 }} onClick={() => setPrintOpen(true)}>
                🖨 Print Schedule
              </button>
            )}
          </>
        )}
      </div>

      {errMsg && (
        <div style={{ background: '#fdeaea', border: '1px solid #e3b3b3', color: '#8b2a2a', borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: 20, fontFamily: 'var(--font-b)', fontSize: 13 }}>
          {errMsg}
        </div>
      )}

      {pendingCount > 0 && (
        <div style={{ background: '#fff8ed', border: '1px solid #f0d28a', color: '#7a5a18', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 24, fontFamily: 'var(--font-b)', fontSize: 14, lineHeight: 1.5 }}>
          <strong style={{ fontFamily: 'var(--font-h)', fontWeight: 800 }}>
            {pendingCount} {pendingCount === 1 ? 'block needs' : 'blocks need'} your response.
          </strong>{' '}
          Please accept or decline each one below so your team lead knows you're confirmed.
        </div>
      )}

      {grouped.length === 0 ? (
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, fontWeight: 300, color: 'var(--muted)' }}>
          {mode === 'positions'
            ? 'No positions assigned yet. Your team lead will assign you soon.'
            : "No schedule items yet. Once you accept your positions, your schedule will appear here."}
        </div>
      ) : grouped.map(({ key, day, items }) => (
        <div key={key || day} style={S.dayBlock}>
          <div style={S.dayLabel}>
            {day}
            {items[0]?.date && (
              <span style={{ fontWeight: 300, letterSpacing: 0, textTransform: 'none', marginLeft: 8 }}>
                {formatDate(items[0].date)}
              </span>
            )}
          </div>
          {items.map((item, i) => {
            const isTransport = item.isTransportation || item.isTransport;
            const teamPill = isMultiTeam && item.teamId && !isTransport
              ? getTeamPillStyle(item.teamId, teamIds)
              : null;
            return (
              <div key={i} style={{ ...S.item, ...(i === items.length - 1 ? { borderBottom: 'none' } : {}) }}>
                <div style={S.time}>
                  {formatTime(item.startTime)}
                  {(isTransport || /shuttle|transport/i.test(item.blockTitle || '')) && item.endTime && (
                    <div style={{ fontWeight: 300, color: 'var(--subtle)' }}>– {formatTime(item.endTime)}</div>
                  )}
                </div>
                <div style={S.detail}>
                  <div style={S.itemName}>
                    {item.blockTitle}
                    {isMultiDept && item.department && (
                      <span style={{ ...S.pill, background: item.department === 'Kids' ? '#6AA292' : '#202020', color: '#fff' }}>
                        {item.department}
                      </span>
                    )}
                    {teamPill && (
                      <span style={{ ...S.pill, background: teamPill.background, color: teamPill.color }}>
                        {item.teamName}
                      </span>
                    )}
                    {item.isMeal && (
                      <span style={{ ...S.pill, background: '#F2DADA', color: '#8B3A3A' }}>Meal</span>
                    )}
                    {isTransport && (
                      <span style={{ ...S.pill, background: '#DDEAF5', color: '#1E4870' }}>Transport</span>
                    )}
                    {item.isTeamItem && item.linkedBlock && (
                      <span style={{ ...S.pill, ...blockPillStyle(item.linkedBlock) }}>{item.linkedBlock}</span>
                    )}
                  </div>
                  {item.positionLabel && (
                    <div style={S.itemLoc}>{item.positionLabel}</div>
                  )}
                  {item.description && (
                    <div style={{ ...S.itemLoc, marginTop: 4, whiteSpace: 'pre-wrap' }}>{item.description}</div>
                  )}
                  {item.assignmentId && (() => {
                    const st = effStatus(item);
                    if (st === 'accepted') return <div style={{ ...S.statusBadge, color: '#1a5c3a' }}>✓ Accepted</div>;
                    if (st === 'declined') return <div style={{ ...S.statusBadge, color: '#900' }}>✗ Declined</div>;
                    return (
                      <div style={S.actions}>
                        <button
                          className="btn btn-xs btn-dark"
                          disabled={busyId === item.assignmentId}
                          onClick={() => respond(item.assignmentId, 'accepted')}
                        >
                          {busyId === item.assignmentId ? 'Saving…' : 'Accept'}
                        </button>
                        <button
                          className="btn btn-xs btn-danger"
                          disabled={busyId === item.assignmentId}
                          onClick={() => { setDeclineTarget(item.assignmentId); setDeclineReason(''); }}
                        >
                          Decline
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {canPrint && (
        <PrintScheduleModal
          open={printOpen}
          onClose={() => setPrintOpen(false)}
          groups={printGroups}
          teamName={person?.name || 'My Schedule'}
        />
      )}

      <div style={S.footer}>
        <div style={S.kicker}>Questions?</div>
        <div style={S.footerNote}>Contact your team lead or stop by the conference help desk.</div>
        <div style={{ ...S.footerNote, marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          {personScheduleLabel(personId)} · Updates automatically
        </div>
      </div>

      {declineTarget && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}
          onClick={e => { if (e.target === e.currentTarget) { setDeclineTarget(null); setDeclineReason(''); } }}
        >
          <div style={{ background: 'var(--white)', borderRadius: 'var(--radius)', width: '100%', maxWidth: 440, overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontFamily: 'var(--font-h)', fontSize: 15, fontWeight: 800, color: 'var(--black)' }}>Decline This Block</div>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-h)', fontSize: 20, color: 'var(--muted)', lineHeight: 1 }} onClick={() => { setDeclineTarget(null); setDeclineReason(''); }}>×</button>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, fontWeight: 300, color: 'var(--mid)', marginBottom: 16, lineHeight: 1.6 }}>
                Let your team lead know why you can't make this one so they can find a replacement.
              </div>
              <div className="form-group">
                <label className="form-label">Reason (optional)</label>
                <textarea
                  className="form-input"
                  rows={3}
                  placeholder="e.g. I have a conflict that morning…"
                  value={declineReason}
                  onChange={e => setDeclineReason(e.target.value)}
                  style={{ resize: 'vertical', fontFamily: 'var(--font-b)', fontWeight: 300 }}
                />
              </div>
            </div>
            <div style={{ padding: '12px 24px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => { setDeclineTarget(null); setDeclineReason(''); }}>Go Back</button>
              <button className="btn btn-danger" onClick={confirmDecline}>Confirm Decline</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Kids: My Group sub-view (small group leaders) ────────────────────────────
// Their kids, allergies highlighted TBCO yellow, parent contact, and the
// group's rotation path. Read-only — changes go through the Kids staff.

function kidsTextOn(hex) {
  const h = (hex || '').replace('#', '');
  if (h.length < 6) return '#fff';
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62 ? '#202020' : '#fff';
}

function kidsFormatPhone(v) {
  if (!v) return '';
  const digits = String(v).replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return String(v);
}

// "na" / "N/A" / "none" mean no allergy.
function kidsCleanAllergy(v) {
  const s = String(v || '').trim();
  if (/^(none|n\/?a|nka|n\.k\.a\.?|no|nothing|nope)[.!]?$/i.test(s)) return '';
  return s;
}

function MyGroupView({ group }) {
  if (!group) return null;
  const color = group.groupColor || '#6AA292';
  return (
    <div style={{ padding: '28px 32px 60px', maxWidth: 760 }}>
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', background: '#fff', marginBottom: 20 }}>
        <div style={{ background: color, color: kidsTextOn(color), padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontFamily: 'var(--font-h)', fontWeight: 900, fontSize: 16 }}>{group.groupName} Group</span>
          <span style={{ fontFamily: 'var(--font-h)', fontWeight: 700, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.9 }}>
            {group.program} · {group.children.length} kids
          </span>
        </div>
        {group.children.length === 0 ? (
          <div style={{ padding: '24px 20px', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>
            No kids in your group yet — check back closer to conference.
          </div>
        ) : group.children.map((c, i) => (
          <div key={i} style={{ padding: '12px 20px', borderBottom: i < group.children.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 800, color: 'var(--black)' }}>
                {c.name}
                {c.isNew && <span className="pill" style={{ background: '#6AA292', color: '#fff', fontSize: 9, marginLeft: 6 }}>New</span>}
              </div>
              <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>
                {c.grade ? `Grade ${c.grade}` : ''}{c.grade && c.gender ? ' · ' : ''}{c.gender || ''}
                {c.parentName ? ` · ${c.parentName}${c.parentContact ? ` (${kidsFormatPhone(c.parentContact)})` : ''}` : ''}
              </div>
            </div>
            {kidsCleanAllergy(c.allergies) ? (
              <span style={{ background: '#FFDB7C', color: '#202020', borderRadius: 4, padding: '3px 9px', fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700 }}>
                {kidsCleanAllergy(c.allergies)}
              </span>
            ) : null}
          </div>
        ))}
      </div>

      {group.rotationPath && group.rotationPath.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: '#fff', padding: 18 }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>
            Your Rotations — where your group goes
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', maxWidth: 380 }}>
            <tbody>
              {group.rotationPath.map(r => (
                <tr key={r.session}>
                  <td style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)', padding: '8px 12px 8px 0', borderBottom: '1px solid var(--border)' }}>{r.session}</td>
                  <td style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--mid)', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>{r.activity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Kids: availability link sub-view ─────────────────────────────────────────
// Main-conference SLs: self-serve link to the preferred-serving-times grid
// (day × time of day), mirroring the Kids availability tab.
function ServingAvailabilityLinkView({ personId }) {
  return (
    <div style={{ padding: '40px 32px', maxWidth: 560 }}>
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderTop: '4px solid var(--black)', borderRadius: 8, padding: 24 }}>
        <div style={{ fontFamily: 'var(--font-h)', fontSize: 17, fontWeight: 900, color: 'var(--black)', marginBottom: 8 }}>
          Tell your team when you can serve
        </div>
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, color: 'var(--mid)', lineHeight: 1.7, marginBottom: 18 }}>
          Mark the days and times of day you're available and your team lead will schedule around them.
          You can update it any time before the schedule is built.
        </div>
        <a
          href={`/serve/${personId}`}
          style={{ display: 'inline-block', fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 14, padding: '12px 22px', borderRadius: 6, background: 'var(--black)', color: '#fff', textDecoration: 'none' }}
        >
          Open My Availability Form
        </a>
      </div>
    </div>
  );
}

function KidsAvailabilityLinkView({ personId }) {
  return (
    <div style={{ padding: '40px 32px', maxWidth: 560 }}>
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderTop: '4px solid #6AA292', borderRadius: 8, padding: 24 }}>
        <div style={{ fontFamily: 'var(--font-h)', fontSize: 17, fontWeight: 900, color: 'var(--black)', marginBottom: 8 }}>
          Tell the Kids team your plans
        </div>
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, color: 'var(--mid)', lineHeight: 1.7, marginBottom: 18 }}>
          Pick how many sessions you can serve, then check every session you're free for — the team
          schedules you into the most helpful ones. You can update it any time before the schedule is built.
        </div>
        <a
          href={`/avail/${personId}`}
          style={{ display: 'inline-block', fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 14, padding: '12px 22px', borderRadius: 6, background: '#FFDB7C', color: '#202020', textDecoration: 'none' }}
        >
          Open My Availability Form
        </a>
      </div>
    </div>
  );
}

// ── SL Manual sub-view ────────────────────────────────────────────────────────

function SlManualView({ slManual }) {
  if (!slManual?.url) {
    return (
      <div style={{ height: 'calc(100vh - 60px)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off)' }}>
        <div style={{ textAlign: 'center', padding: '40px 24px' }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 16, fontWeight: 900, color: 'var(--black)', marginBottom: 8 }}>No Manual Uploaded Yet</div>
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)' }}>Your admin will add the SL Manual once it's ready.</div>
        </div>
      </div>
    );
  }
  return (
    <div style={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: 52, flexShrink: 0, background: 'var(--black)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px' }}>
        <span style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'white' }}>
          {slManual.label || 'Servant Leader Manual'}
        </span>
        <a href={slManual.url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.55)', textDecoration: 'none' }}>
          Open in new tab ↗
        </a>
      </div>
      <iframe
        src={embedUrl(slManual.url)}
        style={{ flex: 1, border: 'none', width: '100%' }}
        title={slManual.label || 'SL Manual'}
        allow="autoplay"
      />
    </div>
  );
}

// ── Team Info sub-view ────────────────────────────────────────────────────────

function TeamInfoView({ team, teamDocs, teamBlocks }) {
  const docs = teamDocs || [];
  const blocks = teamBlocks || [];
  const manualDoc = docs.find(d => d.id === 'team-manual');
  const extraDocs = docs.filter(d => d.id !== 'team-manual');

  const tabs = [
    { id: 'manual', label: 'Team Manual' },
    { id: 'schedule', label: 'Full Team Schedule' },
    ...extraDocs.map(d => ({ id: `doc-${d.id}`, label: d.label, doc: d })),
  ];

  const [activeTab, setActiveTab] = useState('manual');

  const tabStyle = (active) => ({
    fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700,
    padding: '10px 18px', cursor: 'pointer', border: 'none', background: 'none',
    color: active ? 'var(--black)' : 'var(--muted)',
    borderBottom: active ? '2px solid var(--black)' : '2px solid transparent',
    marginBottom: -2, whiteSpace: 'nowrap',
  });

  function renderDocViewer(doc) {
    if (!doc?.url) {
      return (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off)' }}>
          <div style={{ textAlign: 'center', padding: '40px 24px' }}>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 16, fontWeight: 900, color: 'var(--black)', marginBottom: 8 }}>No Link Added Yet</div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)' }}>Your team lead will add this document once it's ready.</div>
          </div>
        </div>
      );
    }
    return (
      <>
        <div style={{ height: 46, flexShrink: 0, background: 'var(--black)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px' }}>
          <span style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'white' }}>{doc.label}</span>
          <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.55)', textDecoration: 'none' }}>
            Open in new tab ↗
          </a>
        </div>
        <iframe src={embedUrl(doc.url)} style={{ flex: 1, border: 'none', width: '100%', minHeight: 0 }} title={doc.label} allow="autoplay" />
      </>
    );
  }

  function renderTeamSchedule() {
    const S = {
      dayLabel: { fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--black)', paddingBottom: 10, borderBottom: '2px solid var(--black)', marginBottom: 0 },
      blockItem: { display: 'flex', gap: 18, padding: '14px 0', borderBottom: '1px solid var(--border)', alignItems: 'flex-start' },
      blockTime: { fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, color: 'var(--muted)', width: 72, flexShrink: 0, paddingTop: 3 },
      blockTitle: { fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 800, color: 'var(--black)', marginBottom: 2 },
      blockNotes: { fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)' },
    };

    if (blocks.length === 0) {
      return <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, fontWeight: 300, color: 'var(--muted)' }}>No team blocks yet.</div>;
    }
    const groupedBlocks = groupByDay(blocks.map(b => ({ ...b, startTime: b.startTime || '' })));

    return (
      <div>
        {groupedBlocks.map(({ key, day, items }) => (
          <div key={key || day} style={{ marginBottom: 28 }}>
            <div style={S.dayLabel}>
              {day}
              {items[0]?.date && (
                <span style={{ fontWeight: 300, letterSpacing: 0, textTransform: 'none', marginLeft: 8 }}>
                  {formatDate(items[0].date)}
                </span>
              )}
            </div>
            {items.map((b, i) => (
              <div key={i} style={{ ...S.blockItem, ...(i === items.length - 1 ? { borderBottom: 'none' } : {}) }}>
                <div style={S.blockTime}>{formatTime(b.startTime)}</div>
                <div>
                  <div style={S.blockTitle}>{b.blockTitle}</div>
                  {b.notes && <div style={S.blockNotes}>{b.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  const isDocTab = activeTab !== 'schedule';

  return (
    <div style={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header: team name + tabs */}
      <div style={{ flexShrink: 0, background: 'var(--white)', borderBottom: '2px solid var(--border)', padding: '20px 32px 0' }}>
        <div style={{ fontFamily: 'var(--font-h)', fontSize: 16, fontWeight: 900, color: 'var(--black)', marginBottom: 12 }}>
          {team?.name || 'Team Info'}
        </div>
        <div style={{ display: 'flex' }}>
          {tabs.map(t => (
            <button key={t.id} style={tabStyle(activeTab === t.id)} onClick={() => setActiveTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {activeTab === 'schedule' ? (
        <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px 60px', maxWidth: 660 }}>
          {renderTeamSchedule()}
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {activeTab === 'manual'
            ? renderDocViewer(manualDoc)
            : renderDocViewer(tabs.find(t => t.id === activeTab)?.doc)
          }
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PersonSchedulePage() {
  const { personId } = useParams();
  const isDemo = !!DEMO_MAP[personId];
  // Demos start with their sample data immediately — no loading state, no blank screen
  const [data, setData] = useState(isDemo ? DEMO_MAP[personId] : null);
  const [loading, setLoading] = useState(!isDemo);
  const [error, setError] = useState(null);

  // Determine if mobile (< 700px)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 700);
  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth < 700); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const lastFetchRef = useRef(Date.now());
  useEffect(() => {
    if (!personId) return;
    let cancelled = false; // guard: don't let a stale response overwrite a newer link's data
    if (isDemo) {
      // The main-app demo upgrades with live Events Team data; the kids demos
      // stay on their sample data (they exist to preview the kids experience).
      if (personId === 'demo') {
        getDemoData()
          .then(d => { if (!cancelled) setData(d); })
          .catch(() => { /* keep DEMO_DATA */ });
      }
      return () => { cancelled = true; };
    }
    setLoading(true);
    setError('');
    // The Refresh button sets this flag so an explicit refresh bypasses the
    // server-side cache; normal loads take the fast cached payload.
    let wantFresh = false;
    try {
      if (sessionStorage.getItem('psFresh')) { wantFresh = true; sessionStorage.removeItem('psFresh'); }
    } catch { /* private mode */ }
    // GAS gets slammed on conference days: a single failed fetch used to
    // dead-end on "Schedule not found," telling people their LINK was broken
    // when the server was just busy. Retry with backoff and only blame the
    // link when the backend explicitly says this person doesn't exist.
    const attempt = (n) => {
      getPersonSchedule(personId, wantFresh && n === 1)
        .then(d => { if (!cancelled) { setData(d); setError(''); setLoading(false); lastFetchRef.current = Date.now(); } })
        .catch(err => {
          if (cancelled) return;
          console.error(err);
          if (/person not found/i.test(String(err?.message || ''))) {
            setError('Schedule not found. Please check your link or contact your team lead.');
            setLoading(false);
          } else if (n < 4) {
            setTimeout(() => { if (!cancelled) attempt(n + 1); }, 2000 + n * 4000);
          } else {
            setError("The schedule system is very busy right now — your link is fine. Tap Retry or try again in a minute.");
            setLoading(false);
          }
        });
    };
    attempt(1);
    return () => { cancelled = true; };
  }, [personId, isDemo]);

  // Phones reopening the saved page from the home screen often resume the
  // old view without reloading — silently refetch when the page becomes
  // visible again and the data is more than 5 minutes old.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      if (isDemo || !personId) return;
      if (Date.now() - lastFetchRef.current < 5 * 60 * 1000) return;
      getPersonSchedule(personId)
        .then(d => { lastFetchRef.current = Date.now(); setData(d); })
        .catch(() => { /* keep showing what we have */ });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [personId, isDemo]);

  const person = data?.person;

  // Normalize person.teams to array of { id, name } objects
  const rawTeams = person?.teams || [];
  const personTeams = rawTeams.map(t =>
    typeof t === 'object' ? t : { id: t, name: t }
  );

  const slManual = data?.slManual || null;
  const teamDocs = data?.teamDocs || {};
  const teamBlocks = data?.teamBlocks || {};
  const kidsSmallGroup = data?.kidsSmallGroup || null;
  const onKidsTeam = !!data?.onKidsTeam;

  // Split the SL's items: positions (block assignments they accept/decline) get
  // their own tab; team events/meals/shuttles are the schedule pieces. My
  // Schedule shows ACCEPTED positions plus the pieces — an assigned block only
  // lands on the schedule once the SL has said yes (pending ones live on the
  // My Positions tab until then).
  const allSched = data?.schedule || [];
  const positions = allSched.filter(i => i.assignmentId);
  const pieces = allSched.filter(i => !i.assignmentId);
  const myScheduleItems = [
    // Group blocks (e.g. Worship) are timeless membership, not a schedule entry —
    // their linked team items carry the actual times, so skip the bare block.
    ...positions.filter(i => i.status === 'accepted' && !i.groupBlock),
    ...pieces,
  ];

  // Build nav items. Small group leaders get My Group (their kids + allergies
  // + rotations). Kids servers get the shared view-only info tabs (per Mal:
  // everyone needs detail sheets / daily details; allergies are viewable by
  // all; non-SGLs get the full Small Groups view, SGLs have My Group instead).
  // My Availability is intentionally NOT shown for Kids — once schedules go
  // out, changing availability should be a conversation with the team, not a
  // form edit. Main-app servers keep their availability tab.
  const kidsTeamId = data?.kidsTeamId || '';
  const navItems = [
    { key: 'schedule', label: 'My Schedule' },
    { key: 'positions', label: 'My Positions' },
    ...(kidsSmallGroup ? [{ key: 'my-group', label: 'My Small Group', star: true }] : []),
    ...(onKidsTeam && kidsTeamId ? [
      { key: 'kids-daily', label: 'Daily Details' },
      { key: 'kids-sheets', label: 'Detail Sheets' },
      { key: 'kids-rotations', label: 'Rotations' },
      // No full Small Groups tab on personal links — group info is
      // need-to-know: SGLs see their own group via My Small Group, everyone
      // else sees none (per Mal/Rebecca).
      { key: 'kids-allergies', label: 'Allergies' },
    ] : []),
    ...(onKidsTeam ? [] : [{ key: 'availability', label: 'My Availability' }]),
    ...(slManual ? [{ key: 'sl-manual', label: 'Servant Leader Manual' }] : []),
    ...personTeams.map(t => ({ key: `team-${t.id}`, label: `${t.name} Team Info` })),
  ];

  const [activeView, setActiveView] = useState('schedule');
  // First load with pending positions → land them on the Positions tab to act.
  const autoNavRef = useRef(false);
  useEffect(() => {
    if (autoNavRef.current || !data) return;
    autoNavRef.current = true;
    if ((data.schedule || []).some(i => i.assignmentId && i.status === 'pending')) {
      setActiveView('positions');
    }
  }, [data]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--off)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 15, color: 'var(--muted)' }}>Loading your schedule…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--off)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: 420, textAlign: 'center', padding: '0 24px' }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 18, fontWeight: 900, color: 'var(--black)', marginBottom: 12 }}>
            Schedule not available
          </div>
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 15, color: 'var(--muted)', lineHeight: 1.6 }}>
            {error || 'This link is invalid or has expired. Contact your team lead.'}
          </div>
          <button
            className="btn btn-dark"
            style={{ marginTop: 20, padding: '10px 28px' }}
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const activeNavItem = navItems.find(n => n.key === activeView) || navItems[0];

  // Determine which content to show
  function renderContent() {
    if (activeView === 'schedule') {
      return (
        <ScheduleView
          person={person}
          scheduleItems={myScheduleItems}
          personId={personId}
          isDemo={isDemo}
          mode="schedule"
        />
      );
    }
    if (activeView === 'positions') {
      return (
        <ScheduleView
          person={person}
          scheduleItems={positions}
          personId={personId}
          isDemo={isDemo}
          mode="positions"
        />
      );
    }
    if (activeView === 'my-group') {
      return <MyGroupView group={kidsSmallGroup} />;
    }
    if (activeView === 'kids-availability') {
      return <KidsAvailabilityLinkView personId={personId} />;
    }
    if (activeView === 'kids-daily') {
      return <KidsDailyDetails team={{ id: kidsTeamId }} showToast={() => {}} readOnly />;
    }
    if (activeView === 'kids-sheets') {
      return <KidsDetailSheets showToast={() => {}} readOnly />;
    }
    if (activeView === 'kids-rotations') {
      return <KidsRotationsTab showToast={() => {}} readOnly />;
    }
    if (activeView === 'kids-allergies') {
      return <KidsAllergies team={{ id: kidsTeamId }} showToast={() => {}} readOnly />;
    }
    if (activeView === 'availability') {
      return <ServingAvailabilityLinkView personId={personId} />;
    }
    if (activeView === 'sl-manual') {
      return <SlManualView slManual={slManual} />;
    }
    if (activeView.startsWith('team-')) {
      const teamId = activeView.slice('team-'.length);
      const team = personTeams.find(t => t.id === teamId);
      return (
        <TeamInfoView
          team={team}
          teamDocs={teamDocs[teamId] || []}
          teamBlocks={teamBlocks[teamId] || []}
        />
      );
    }
    return null;
  }

  // ── Chrome palette: kids-team people get the teal Kids look ──────────
  const chrome = onKidsTeam
    ? { bg: '#6AA292', nav: 'rgba(255,255,255,0.75)', sub: 'rgba(255,255,255,0.6)', label: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.18)', activeBg: 'rgba(255,255,255,0.2)', tabIdle: 'rgba(255,255,255,0.7)' }
    : { bg: 'var(--black)', nav: 'rgba(255,255,255,0.5)', sub: 'rgba(255,255,255,0.35)', label: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.1)', activeBg: 'rgba(255,255,255,0.1)', tabIdle: 'rgba(255,255,255,0.45)' };
  // Desktop nav buttons already space children with flex gap; only the mobile
  // tab bar (inline text) needs the star's own margin.
  const star = <span style={{ color: '#FFDB7C' }}>★</span>;
  const starMobile = <span style={{ color: '#FFDB7C', marginRight: 5 }}>★</span>;

  // ── Mobile layout ─────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--white)' }}>
        {/* Mobile tab bar */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 100,
          background: chrome.bg,
          display: 'flex', overflowX: 'auto',
          borderBottom: chrome.border,
        }}>
          {navItems.map(item => (
            <button
              key={item.key}
              onClick={() => setActiveView(item.key)}
              style={{
                fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700,
                padding: '12px 16px', whiteSpace: 'nowrap',
                background: 'none', border: 'none', cursor: 'pointer',
                color: activeView === item.key ? 'white' : chrome.tabIdle,
                borderBottom: activeView === item.key ? '2px solid white' : '2px solid transparent',
              }}
            >
              {item.star && starMobile}{item.label}
            </button>
          ))}
        </div>
        {/* Content */}
        <div style={{ background: 'var(--white)' }}>
          {renderContent()}
        </div>
      </div>
    );
  }

  // ── Desktop layout with sidebar ───────────────────────────
  const SIDEBAR_W = 220;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--off)', display: 'flex' }}>
      {/* Sidebar — teal for kids-team people, dark otherwise */}
      <div style={{
        width: SIDEBAR_W, flexShrink: 0,
        background: chrome.bg,
        position: 'fixed', top: 0, left: 0, bottom: 0,
        display: 'flex', flexDirection: 'column',
        zIndex: 200,
      }}>
        {/* Logo area */}
        <div style={{ padding: '22px 24px 20px', borderBottom: chrome.border }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 900, color: 'white', letterSpacing: '0.02em', lineHeight: 1.2 }}>
            THE BELONGING CO
          </div>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 600, color: chrome.sub, letterSpacing: '0.15em', textTransform: 'uppercase', marginTop: 3 }}>
            {onKidsTeam ? 'KIDS CONFERENCE 2026' : 'CONFERENCE 2026'}
          </div>
        </div>

        {/* Nav */}
        <div style={{ flex: 1, paddingTop: 8 }}>
          <div style={{
            fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: chrome.label,
            padding: '12px 24px 6px',
          }}>
            MY CONFERENCE
          </div>
          {navItems.map(item => {
            const active = activeView === item.key;
            return (
              <button
                key={item.key}
                onClick={() => setActiveView(item.key)}
                style={{
                  fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 600,
                  color: active ? 'white' : chrome.nav,
                  padding: '9px 24px',
                  display: 'flex', alignItems: 'center', gap: 10,
                  cursor: 'pointer', border: 'none',
                  background: active ? chrome.activeBg : 'none',
                  width: '100%', textAlign: 'left',
                  borderRadius: 0,
                }}
              >
                {item.star && star}{item.label}
              </button>
            );
          })}
        </div>

        {/* User area */}
        <div style={{
          borderTop: chrome.border,
          padding: '16px 24px',
        }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, color: 'white', marginBottom: 2 }}>
            {person?.name || ''}
          </div>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: chrome.sub }}>
            SERVANT LEADER
          </div>
        </div>
      </div>

      {/* Main area */}
      <div style={{ marginLeft: SIDEBAR_W, flex: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Topbar */}
        <div style={{
          height: 60, background: 'white',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 32px',
          position: 'sticky', top: 0, zIndex: 100,
        }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 15, fontWeight: 800, color: 'var(--black)' }}>
            {activeNavItem?.label || 'My Schedule'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700,
              background: 'var(--black)', color: 'white',
              borderRadius: 100, padding: '3px 10px',
            }}>2026</span>
            <button
              onClick={() => {
                try { sessionStorage.setItem('psFresh', '1'); } catch { /* private mode */ }
                window.location.reload();
              }}
              title="Reload to pull the latest schedule"
              style={{
                fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700,
                padding: '6px 14px', borderRadius: 'var(--radius)',
                border: '1.5px solid var(--border)', background: 'white',
                color: 'var(--mid)', cursor: 'pointer',
              }}
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, background: 'var(--white)' }}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
